import { useEffect, useState } from 'react';
import { AlertTriangle, PartyPopper } from 'lucide-react';
import { apiPersons, apiDashboard, apiSchedule } from '../api/client.js';
import { formatHHMM } from '../components/DurationInput.jsx';
import {
  todayUtc,
  mondayOf,
  addDays,
  isoDate,
  weekDays,
  fullDayLabel,
  safeColor,
  safeColorAlpha,
} from './dateUtils.js';
import { halfDayLabel } from '../components/HalfDayPicker.jsx';

function pct(v) {
  if (v == null || Number.isNaN(v)) return '—';
  return `${Math.round(v)}%`;
}

/**
 * Ticket #33 — les congés en attente de validation sont retirés de la capacité
 * à planifier ; on l'indique explicitement plutôt que de laisser le jour
 * apparaître comme « non planifié ».
 */
function pendingHint(metric) {
  const d = metric?.pendingTimeOffDays;
  if (!d || d <= 0) return null;
  return `${d} j de congé en attente de validation`;
}

function IndicatorCard({ title, data, hint }) {
  // data : { fillPct, alert, ...heures }. On résout les libellés selon le champ.
  const fill = data?.fillPct;
  const alert = Boolean(data?.alert);
  const overAlert = Boolean(data?.overAlert); // ticket #15 : heures sup (>100%)
  const hours =
    data?.allocatedHours ?? data?.loggedHours ?? 0;
  const cap = data?.capacityHours ?? 0;
  const fillWidth = fill == null ? 0 : Math.min(100, Math.max(0, fill));
  const barColor = alert
    ? 'var(--danger)'
    : overAlert
      ? 'var(--warning)'
      : fill != null && fill >= 100
        ? 'var(--success)'
        : 'var(--accent)';

  return (
    <div className="m-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>
        {alert ? (
          <AlertTriangle size={16} color="var(--danger)" style={{ marginLeft: 'auto' }} />
        ) : overAlert ? (
          <AlertTriangle size={16} color="var(--warning)" style={{ marginLeft: 'auto' }} />
        ) : null}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          marginTop: 6,
        }}
      >
        <span style={{ fontSize: 24, fontWeight: 700 }}>{pct(fill)}</span>
        <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          {formatHHMM(hours)} / {formatHHMM(cap)}
        </span>
      </div>
      <div className="m-bar">
        <div
          className="m-bar__fill"
          style={{ width: `${fillWidth}%`, background: barColor }}
        />
      </div>
      {hint ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 6 }}>{hint}</div>
      ) : null}
    </div>
  );
}

export default function MobileDashboard() {
  const [state, setState] = useState({ loading: true, error: null });
  const [dashboard, setDashboard] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [myPersonId, setMyPersonId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState({ loading: true, error: null });
      // 1) Person liée (peut 404).
      let personId = null;
      try {
        const me = await apiPersons.me();
        personId = me?.id ?? null;
      } catch {
        if (!cancelled) {
          setState({ loading: false, error: 'no-person' });
          return;
        }
      }
      if (cancelled) return;
      setMyPersonId(personId);

      const monday = mondayOf(todayUtc());
      const sunday = addDays(monday, 6);
      try {
        const [dash, sched] = await Promise.all([
          apiDashboard.get('self'),
          apiSchedule.get({
            from: isoDate(monday),
            to: isoDate(sunday),
            personIds: personId ? [personId] : undefined,
            withHolidays: true,
          }),
        ]);
        if (cancelled) return;
        setDashboard(dash);
        setSchedule(sched);
        setState({ loading: false, error: null });
      } catch {
        if (!cancelled) setState({ loading: false, error: 'load' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.loading) return <div className="m-state">Chargement…</div>;
  if (state.error === 'no-person')
    return (
      <div className="m-state">
        Aucune fiche collaborateur n'est liée à votre compte. Contactez un
        administrateur.
      </div>
    );
  if (state.error) return <div className="m-state">Impossible de charger le tableau de bord.</div>;

  const p = dashboard?.personal ?? {};

  return (
    <div>
      <div className="m-section-title" style={{ marginTop: 0 }}>
        Mes indicateurs
      </div>
      {p.schedulingMonth ? (
        <IndicatorCard
          title="Planification (mois complet)"
          data={p.schedulingMonth}
          hint={pendingHint(p.schedulingMonth)}
        />
      ) : null}
      <IndicatorCard
        title="Planification (reste du mois)"
        data={p.scheduling}
        hint={
          [
            p.scheduling?.unplannedWorkingDays != null
              ? `${p.scheduling.unplannedWorkingDays} jour(s) ouvré(s) non planifié(s)`
              : null,
            pendingHint(p.scheduling),
          ]
            .filter(Boolean)
            .join(' · ') || undefined
        }
      />
      <IndicatorCard title="Logtime — semaine précédente" data={p.prevWeek} />
      <IndicatorCard
        title="Logtime — semaine en cours"
        data={p.currentWeek}
        // Ticket #33 — référence semaine entière (#15) + attendu à date.
        hint={
          p.currentWeek?.toDateCapacityHours != null &&
          p.currentWeek.toDateCapacityHours < p.currentWeek.capacityHours
            ? `À date : ${formatHHMM(p.currentWeek.toDateLoggedHours)} / ${formatHHMM(
                p.currentWeek.toDateCapacityHours,
              )}`
            : undefined
        }
      />

      <WeekSummary schedule={schedule} myPersonId={myPersonId} />
    </div>
  );
}

function WeekSummary({ schedule, myPersonId }) {
  if (!schedule) return null;
  const monday = mondayOf(todayUtc());
  const days = weekDays(monday);
  const todayIso = isoDate(todayUtc());

  const allocations = schedule.allocations ?? [];
  const timeOffs = schedule.timeOffs ?? [];
  const holidays = (myPersonId && schedule.holidaysByPerson?.[myPersonId]) || [];
  const holidayByIso = new Map(holidays.map((h) => [String(h.date).slice(0, 10), h]));

  // Une allocation couvre [startDate, endDate] : on l'affiche sur chaque jour
  // de la fenêtre qu'elle recouvre.
  function blocksForDay(iso) {
    const allocs = allocations.filter(
      (a) => String(a.startDate).slice(0, 10) <= iso && iso <= String(a.endDate).slice(0, 10),
    );
    const offs = timeOffs.filter(
      (t) => String(t.startDate).slice(0, 10) <= iso && iso <= String(t.endDate).slice(0, 10),
    );
    return { allocs, offs };
  }

  return (
    <>
      <div className="m-section-title">Ma semaine</div>
      {days.map((d) => {
        const iso = isoDate(d);
        const { allocs, offs } = blocksForDay(iso);
        const holiday = holidayByIso.get(iso);
        if (allocs.length === 0 && offs.length === 0 && !holiday) return null;
        return (
          <div className="m-day" key={iso}>
            <div
              className="m-day__head"
              data-today={iso === todayIso}
              data-holiday={Boolean(holiday)}
            >
              <span>{fullDayLabel(d)}</span>
              {holiday ? (
                <span
                  style={{
                    marginLeft: 'auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    color: 'var(--warning)',
                    fontSize: 12,
                  }}
                >
                  <PartyPopper size={13} /> {holiday.name}
                </span>
              ) : null}
            </div>
            <div className="m-day__body">
              {allocs.map((a) => (
                <div className="m-block" key={a.instanceId}>
                  <span
                    className="m-block__dot"
                    style={{ background: safeColor(a.affaire?.color) }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div className="m-block__name">{a.affaire?.name || a.name || '—'}</div>
                    {a.phase?.name ? (
                      <div className="m-block__sub">{a.phase.name}</div>
                    ) : null}
                  </div>
                  <span className="m-block__hours">{formatHHMM(a.hoursPerDay)}</span>
                </div>
              ))}
              {offs.map((t) => (
                <div
                  className="m-block"
                  key={t.instanceId}
                  style={{ background: safeColorAlpha(t.timeOffType?.colorHex) }}
                >
                  <span
                    className="m-block__dot"
                    style={{ background: safeColor(t.timeOffType?.colorHex, 'var(--text-muted)') }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div className="m-block__name">{t.timeOffType?.name || 'Congé'}</div>
                  </div>
                  <span className="m-block__hours">
                    {t.fullDay
                      ? (halfDayLabel(t, { short: true }) ?? 'Jour')
                      : formatHHMM(t.hoursPerDay)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}
